import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface CompareSystem {
  id: string;
  title: string;
  slug: string;
  short_desc: string;
  cover_image: string;
  category_name: string | null;
  rating_avg: number;
  rating_count: number;
}

interface CompareContextType {
  items: CompareSystem[];
  add: (system: CompareSystem) => void;
  remove: (id: string) => void;
  clear: () => void;
  has: (id: string) => boolean;
  isFull: boolean;
}

const MAX_COMPARE = 3;
const CompareContext = createContext<CompareContextType | null>(null);

export const CompareProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CompareSystem[]>([]);

  const add = useCallback((system: CompareSystem) => {
    setItems((prev) => {
      if (prev.length >= MAX_COMPARE || prev.some((s) => s.id === system.id)) return prev;
      return [...prev, system];
    });
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const has = useCallback((id: string) => items.some((s) => s.id === id), [items]);

  return (
    <CompareContext.Provider value={{ items, add, remove, clear, has, isFull: items.length >= MAX_COMPARE }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error("useCompare must be used inside CompareProvider");
  return ctx;
};
